/**
 * OSM building tags from Overpass, for the vector tiles being streamed.
 *
 * One shared service on the main thread, rather than a fetch per worker, so
 * that the four vector workers asking for four neighbouring tiles make one
 * request. Tags are fetched in blocks a zoom level coarser than the vector
 * tiles — a z13 block is four z14 tiles, ~2.6 km across in Sweden — which
 * quarters the request count against a shared community server, and each
 * block is kept in the browser's Cache Storage for a month, so a second visit
 * to a town asks Overpass for nothing at all.
 *
 * Tags are an enhancement, never a dependency. A tile waits a bounded time for
 * its block and then builds without, and is rebuilt through `onLate` when the
 * block lands; a server that keeps failing is left alone for a growing
 * cool-down instead of being hammered by every tile.
 */

import type { TileKey } from '../core/geo/tilekey.js';
import { tileBounds } from '../core/geo/tilekey.js';
import type { TileBounds } from '../core/geo/tilekey.js';
import type { BuildingTags } from '../core/sources/osmbuildings.js';
import { overpassUrl, parseOverpassCsv, tagsWithin } from '../core/sources/osmbuildings.js';

export interface BuildingTagOptions {
  /** Overpass interpreter URLs, tried in order when one is busy or down. */
  endpoints: string[];
  /** Zoom of the blocks fetched. Coarser means fewer, bigger requests. */
  blockZoom: number;
  /** How long a tile waits for its tags before building without, ms. */
  waitMs: number;
  /** Requests in flight at once. Overpass grants each client two slots. */
  concurrency: number;
  /** How long a cached block stays fresh, days. */
  cacheDays: number;
}

export const DEFAULT_BUILDING_TAG_OPTIONS: BuildingTagOptions = {
  endpoints: [
    'https://overpass-api.de/api/interpreter',
    'https://overpass.private.coffee/api/interpreter',
    'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
  ],
  blockZoom: 13,
  // Short: roads and trees ride in the same tile and should not wait on
  // Overpass. A tile that gives up is rebuilt when its block lands.
  waitMs: 2_500,
  concurrency: 2,
  cacheDays: 30,
};

const FETCHED_HEADER = 'x-wsg-fetched';
const MAX_BLOCKS = 48;
const REQUEST_TIMEOUT_MS = 30_000;
/** Times a failed block is asked for again while tiles wait on it. */
const MAX_RETRIES = 5;

/** What one kind of Overpass data is: how it is asked for, read, cut to a tile, and kept. */
export interface OverpassSpec<T> {
  url(endpoint: string, bounds: TileBounds): string;
  parse(text: string): T[];
  /** The items of a block within (a little past) one tile's bounds. */
  within(items: readonly T[], bounds: TileBounds): T[];
  /** How many items count for the stats: the buildings, not the staircases with them. */
  count(items: readonly T[]): number;
  /** The Cache Storage the answers are kept in, and their content type there. */
  cacheName: string;
  contentType: string;
}

/**
 * Overpass data for the tiles being streamed, a block at a time: queued,
 * cached, retried and cooled down as below. `BuildingTagService` is one,
 * `loaders/osmLineService.ts` another.
 */
export class OverpassBlockService<T> {
  /** Blocks by `z/x/y`, in insertion order for eviction. */
  private readonly blocks = new Map<string, Promise<T[] | undefined>>();
  private readonly queue: Array<() => void> = [];
  private active = 0;
  private endpoint = 0;
  private failures = 0;
  private coolUntil = 0;
  /** `items`: what the spec counts, e.g. the buildings. */
  readonly stats = { requests: 0, cacheHits: 0, failures: 0, items: 0 };
  /**
   * Called with the tiles that gave up waiting for a block, once that block
   * does arrive, so they can be rebuilt with their tags.
   */
  onLate?: (tiles: TileKey[]) => void;
  /** Tiles built without tags, by the block they were waiting for. */
  private readonly late = new Map<string, TileKey[]>();

  constructor(private readonly spec: OverpassSpec<T>, private readonly options: BuildingTagOptions = DEFAULT_BUILDING_TAG_OPTIONS) {}

  /**
   * The tags within one tile, or undefined when they are not available in
   * time. Never rejects, except with an `AbortError` when `signal` fires.
   */
  async forTile(key: TileKey, signal: AbortSignal): Promise<T[] | undefined> {
    const blockKey = this.blockKey(key);
    if (Date.now() < this.coolUntil && !this.blocks.has(blockKey.id)) return undefined;
    const block = this.block(blockKey);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let onAbort: (() => void) | undefined;
    try {
      const tags = await Promise.race([
        block,
        new Promise<undefined>((resolve) => { timer = setTimeout(() => resolve(undefined), this.options.waitMs); }),
        new Promise<never>((_, reject) => {
          onAbort = () => {
            const error = new Error('Aborted');
            error.name = 'AbortError';
            reject(error);
          };
          if (signal.aborted) onAbort();
          else signal.addEventListener('abort', onAbort, { once: true });
        }),
      ]);
      if (tags === undefined) {
        this.markLate(blockKey.id, key, block);
        return undefined;
      }
      return this.spec.within(tags, tileBounds(key));
    } finally {
      clearTimeout(timer);
      if (onAbort !== undefined) signal.removeEventListener('abort', onAbort);
    }
  }

  /**
   * Remember a tile that built without its block, to report when it lands.
   * A block that fails is asked for again after the cool-down, a few times,
   * for as long as tiles are waiting on it.
   */
  private markLate(id: string, key: TileKey, block: Promise<T[] | undefined>): void {
    const waiting = this.late.get(id);
    if (waiting !== undefined) {
      if (!waiting.some(k => k.x === key.x && k.y === key.y && k.z === key.z)) waiting.push(key);
      return;
    }
    this.late.set(id, [key]);
    this.watch(id, block, 0);
  }

  private watch(id: string, block: Promise<T[] | undefined>, retries: number): void {
    void block.then((tags) => {
      const tiles = this.late.get(id);
      if (tiles === undefined) return;
      if (tags !== undefined) {
        this.late.delete(id);
        this.onLate?.(tiles);
        return;
      }
      if (retries >= MAX_RETRIES) {
        this.late.delete(id);
        return;
      }
      const wait = Math.max(0, this.coolUntil - Date.now()) + 1000 + Math.random() * 4000;
      setTimeout(() => {
        const [z, x, y] = id.split('/').map(Number);
        this.watch(id, this.block({ id, key: { z, x, y } }), retries + 1);
      }, wait);
    });
  }

  private blockKey(key: TileKey): { id: string; key: TileKey } {
    const shift = Math.max(0, key.z - this.options.blockZoom);
    const z = key.z - shift;
    const block = { z, x: key.x >> shift, y: key.y >> shift };
    return { id: `${block.z}/${block.x}/${block.y}`, key: block };
  }

  private block({ id, key }: { id: string; key: TileKey }): Promise<T[] | undefined> {
    const known = this.blocks.get(id);
    if (known !== undefined) return known;
    const pending = this.schedule(() => this.load(key)).then((tags) => {
      // A failure is forgotten, so the block is asked for again once the
      // server has had its cool-down; a success stays.
      if (tags === undefined) this.blocks.delete(id);
      return tags;
    });
    this.blocks.set(id, pending);
    while (this.blocks.size > MAX_BLOCKS) {
      const oldest = this.blocks.keys().next().value;
      if (oldest === undefined) break;
      this.blocks.delete(oldest);
    }
    return pending;
  }

  private schedule<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        this.active++;
        task().then(resolve, reject).finally(() => {
          this.active--;
          this.queue.shift()?.();
        });
      };
      if (this.active < this.options.concurrency) run();
      else this.queue.push(run);
    });
  }

  private async load(key: TileKey): Promise<T[] | undefined> {
    const bounds = tileBounds(key);
    // The cache is keyed on the first endpoint's URL whichever server
    // answered: the data is the same, and the key must be stable.
    const cacheKey = this.spec.url(this.options.endpoints[0], bounds);
    const cached = await readCache(this.spec.cacheName, cacheKey, this.options.cacheDays);
    if (cached !== undefined) {
      this.stats.cacheHits++;
      return this.parsed(cached);
    }
    if (Date.now() < this.coolUntil) return undefined;

    const { endpoints } = this.options;
    for (let attempt = 0; attempt < endpoints.length; attempt++) {
      const endpoint = endpoints[(this.endpoint + attempt) % endpoints.length];
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        this.stats.requests++;
        const response = await fetch(this.spec.url(endpoint, bounds), { signal: controller.signal });
        if (!response.ok) continue;
        const text = await response.text();
        // Overpass reports a query that ran out of time or memory as a
        // remark in an otherwise successful response. Not data: try the next.
        if (/runtime error|Dispatcher_Client/.test(text.slice(0, 2000))) continue;
        this.endpoint = (this.endpoint + attempt) % endpoints.length;
        this.failures = 0;
        void writeCache(this.spec.cacheName, this.spec.contentType, cacheKey, text);
        return this.parsed(text);
      } catch {
        // Timed out or refused: try the next server.
      } finally {
        clearTimeout(timer);
      }
    }
    this.stats.failures++;
    this.failures++;
    this.coolUntil = Date.now() + Math.min(600_000, 30_000 * 2 ** (this.failures - 1));
    return undefined;
  }

  private parsed(text: string): T[] {
    const items = this.spec.parse(text);
    this.stats.items += this.spec.count(items);
    return items;
  }
}

/** The OSM building tags (and staircases, chargers) of the tiles, as `osmbuildings.ts` asks for them. */
export class BuildingTagService extends OverpassBlockService<BuildingTags> {
  constructor(options: BuildingTagOptions = DEFAULT_BUILDING_TAG_OPTIONS) {
    super({
      url: overpassUrl,
      parse: parseOverpassCsv,
      // A little past the tile's edge, for the outlines clipped there.
      within: (tags, bounds) => tagsWithin(tags, bounds, 0.0008),
      count: (tags) => tags.filter(t => t.steps === undefined && t.charger === undefined).length,
      cacheName: 'wsg-osm-buildings-v1',
      contentType: 'text/csv',
    }, options);
  }
}

async function readCache(name: string, key: string, days: number): Promise<string | undefined> {
  try {
    if (typeof caches === 'undefined') return undefined;
    const cache = await caches.open(name);
    const hit = await cache.match(key);
    if (hit === undefined) return undefined;
    const fetched = Number(hit.headers.get(FETCHED_HEADER));
    if (!Number.isFinite(fetched) || Date.now() - fetched > days * 86_400_000) return undefined;
    return await hit.text();
  } catch {
    return undefined;
  }
}

async function writeCache(name: string, contentType: string, key: string, text: string): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(name);
    await cache.put(key, new Response(text, {
      headers: { 'content-type': contentType, [FETCHED_HEADER]: String(Date.now()) },
    }));
  } catch {
    // Storage full or unavailable: the next visit fetches again.
  }
}
